/**
 * Lead pipeline stages + their colors. Shared by the dashboard and leads views
 * so the pipeline stays consistent in one place.
 */

/**
 * ORDER IS THE PIPELINE. Everything that renders a stage list reads this array,
 * so the dashboard columns, the All Clients filter and the lead-file select all
 * follow it without being told twice.
 *
 * The three that describe a job in progress are distinct events, not shades of
 * done:
 *   Booked    confirmed, not yet picked up
 *   Invoiced  invoiced, not yet paid in full
 *   Closed    client paid and job delivered — set by an ADMIN, never
 *             automatically; it makes contractor pay and commission Owed now
 *
 * Booked -> Invoiced happens BY ITSELF when a non-void invoice is generated
 * (from Booked only). Only an admin moves a job to or from Closed; any other
 * stage can be set by hand.
 */
export const STAGES = [
  'New Lead',
  'In Progress',
  'Opportunity',
  'Booked',
  'Invoiced',
  'Closed',
  'Bad Lead',
  'Lost',
] as const;

/** CSS variable slug per stage (src/theme.css: --crm-stage-<slug> text, --crm-stage-<slug>-bg pill). */
const STAGE_SLUG: Record<string, string> = {
  'New Lead': 'new',
  'In Progress': 'progress',
  Opportunity: 'opportunity',
  Booked: 'booked',
  Invoiced: 'invoiced',
  Closed: 'closed',
  'Bad Lead': 'bad',
  Lost: 'lost',
};

/** A stage's text colour (theme variable). Unrecognised stages (e.g. migrated data) read as Lost. */
export const STAGE_COLORS: Record<string, string> = Object.fromEntries(
  Object.entries(STAGE_SLUG).map(([s, slug]) => [s, `var(--crm-stage-${slug})`]),
);

/** Color for a stage, with a safe fallback for unrecognized values (e.g. migrated data). */
export function stageColor(stage: string | null | undefined): string {
  return (stage && STAGE_COLORS[stage]) || 'var(--crm-stage-lost)';
}

/** The pill background that goes with stageColor — a pair chosen to read at AA. */
export function stageBg(stage: string | null | undefined): string {
  const slug = (stage && STAGE_SLUG[stage]) || 'lost';
  return `var(--crm-stage-${slug}-bg)`;
}
